import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Search, Mail, Phone, Users } from 'lucide-react';

interface DirectoryMember {
  id: string;
  firstName: string;
  lastName: string;
  hebrewName: string | null;
  email: string | null;
  phone: Array<{ type: string; number: string }> | null;
  photoUrl: string | null;
  household: { id: string; name: string } | null;
}

interface DirectoryResponse {
  data: DirectoryMember[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export function DirectoryPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['directory', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '24',
        sortBy: 'lastName',
        sortOrder: 'asc',
      });
      if (search) params.set('search', search);
      const response = await api.get<DirectoryMember[]>(`/members/directory?${params}`);
      return response as unknown as DirectoryResponse;
    },
  });

  const members = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Member Directory</h1>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search members..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-64 rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <Users className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No members found</h3>
          <p className="mt-2 text-gray-500">
            {search ? 'Try adjusting your search terms' : 'The directory is empty'}
          </p>
        </div>
      ) : (
        <>
          {/* Member Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {members.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-600">
                Showing {(meta.page - 1) * meta.limit + 1} to{' '}
                {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} members
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= meta.totalPages}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MemberCard({ member }: { member: DirectoryMember }) {
  const primaryPhone = member.phone?.[0];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {member.photoUrl ? (
          <img
            src={member.photoUrl}
            alt=""
            className="h-12 w-12 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-lg font-semibold flex-shrink-0">
            {member.firstName.charAt(0)}
            {member.lastName.charAt(0)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 truncate">
            {member.firstName} {member.lastName}
          </h3>
          {member.hebrewName && (
            <p className="text-sm text-gray-500 truncate" dir="rtl">
              {member.hebrewName}
            </p>
          )}
          {member.household && (
            <p className="text-xs text-gray-400 truncate mt-1">
              {member.household.name}
            </p>
          )}
        </div>
      </div>

      {(member.email || primaryPhone) && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 truncate"
            >
              <Mail className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{member.email}</span>
            </a>
          )}
          {primaryPhone && (
            <a
              href={`tel:${primaryPhone.number}`}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600"
            >
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span>{primaryPhone.number}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}
